import type { HardwarePackage, SerializedHardwarePackage, SerializedInventoryMeter } from '@enmon/commons/lib/tools';
import { buildQuery } from '@enmon/commons/lib/tools/utils/buildQuery';
import type { StrapiApiResponse } from 'api/types/strapiApiResponse';
import { BASE_REST_API_URL } from 'common/constants';
import type { BaseAPIParams } from 'lib/utils/BaseAPIParams';
import { BaseApiClient } from './baseApiClient';

export type HardwarePackageDto = Pick<
  SerializedHardwarePackage,
  'name' | 'hardwares' | 'reseller' | 'priority' | 'query'
>;

export type UpdateHardwarePackageDto = Partial<HardwarePackageDto>;

export type FindOneHardwarePackageArg = HardwarePackage['id'];
export type FindMeterCompatibleHardwarePackagesArg = { meters: SerializedInventoryMeter['id'][]; signal?: AbortSignal };
export type DeleteHardwarePackageArg = HardwarePackage['id'];
export type CreateHardwarePackageArg = HardwarePackageDto;
export interface UpdateHardwarePackageArgs {
  id: HardwarePackage['id'];
  values: UpdateHardwarePackageDto;
}

export type HardwarePackageResult = StrapiApiResponse<SerializedHardwarePackage>;
export type HardwarePackagesResult = StrapiApiResponse<SerializedHardwarePackage>;
export type HardwarePackagesMeterCompatibleResult = StrapiApiResponse<SerializedHardwarePackage>;
export type HardwarePackagesFilter = BaseAPIParams<SerializedHardwarePackage> & {
  hardwarePackages?: SerializedHardwarePackage['id'][];
  hardwares?: SerializedHardwarePackage['id'][];
};

export class HardwarePackagesApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('hardware-packages', BASE_REST_API_URL, jwt);
  }

  public async find(filter: HardwarePackagesFilter, signal: AbortSignal | undefined): Promise<HardwarePackagesResult> {
    const { hardwarePackages = [], hardwares = [], fields, page, pageSize, populate, sort } = filter ?? {};
    const response = await this.axios.get<HardwarePackagesResult>(``, {
      params: buildQuery<SerializedHardwarePackage>({
        sort,
        pagination: {
          page,
          pageSize,
        },
        filters: {
          id: {
            $in: hardwarePackages,
          },
          hardwares: {
            id: {
              $in: hardwares,
            },
          },
        },
        fields,
        populate,
      }),
      signal,
    });
    return response.data;
  }

  public async findOne(
    id: FindOneHardwarePackageArg,
    signal: AbortSignal | undefined,
  ): Promise<{ data: HardwarePackageResult }> {
    const response = await this.axios.get<{ data: HardwarePackageResult }>(`${id}`, { signal });

    return response.data;
  }

  public async findCompatibleWithMeters({
    meters,
    signal,
  }: FindMeterCompatibleHardwarePackagesArg): Promise<StrapiApiResponse<SerializedHardwarePackage>> {
    const response = await this.axios.get<HardwarePackagesMeterCompatibleResult>(`find-compatible-with-meters`, {
      params: { meters },
      signal,
    });

    return response.data;
  }

  public async create(values: CreateHardwarePackageArg): Promise<SerializedHardwarePackage> {
    const response = await this.axios.post<SerializedHardwarePackage>('', { data: values });

    return response.data;
  }

  public async update({ id, values }: UpdateHardwarePackageArgs): Promise<SerializedHardwarePackage> {
    const response = await this.axios.put<SerializedHardwarePackage>(`${id}`, { data: values });

    return response.data;
  }

  public async delete(id: DeleteHardwarePackageArg): Promise<SerializedHardwarePackage> {
    const response = await this.axios.delete<SerializedHardwarePackage>(`${id}`);

    return response.data;
  }
}
